import { defineStore } from "pinia";
import SystemMetaResource from "~/models/leyden-mobile/SystemMeta";
import StudentResource from "~/models/leyden-mobile/Student";
import StudentMetaResource from "~/models/leyden-mobile/StudentMeta";
import WatchlistStudentResource from "~/models/leyden-mobile/WatchlistStudent";
import StudentNoteResource from "~/models/leyden-mobile/Note";
import BuildingResource from "~/models/leyden-mobile/Building";
import StaffResource from "~/models/leyden-mobile/Staff";
import NotificationResource from "~/models/leyden-mobile/Notification";

export const useLeydenMobileStore = defineStore("leydenmobile", {
  state: () => ({
    attendance_data: {},
    building_options: [
      { value: 100, label: "East Leyden" },
      { value: 200, label: "West Leyden" },
    ],
    student_view_options: [
      { value: "todays_schedule", label: "Today's Schedule" },
      { value: "schedule", label: "Student Schedule" },
      { value: "contact_info", label: "Contacts & Sibling Information" },
      { value: "notes", label: "Notes" },
      { value: "personal_info", label: "Personal Information" },
      { value: "res_reg", label: "Residency / Registration" },
      { value: "current_averages", label: "Current Averages" },
      { value: "testing", label: "Longitudinal Tests" },
      { value: "progress_reports", label: "Progress Reports" },
      { value: "report_cards", label: "Report Cards" },
      { value: "discipline_history", label: "Discipline History" },
      { value: "attendance_history", label: "Attendance History" },
      { value: "entry_withdrawal", label: "Entry / Withdrawal" },
      { value: "unpaid_fines", label: "Unpaid Fines" },
    ],
    student_filter_options: null,
    note_type_options: [],
    default_student_view: "todays_schedule",
    default_building: null,
    loading_studentlist: false,
    studentlist_students: [],
    loading_watchlist: false,
    watchlist_students: [],
    loading_notifications: false,
    notifications: [],
    building_rooms: [],
    loading_student: false,
    current_student: null,
    loading_staff: false,
    current_staff: null,
    current_staff_schedule: [],
    loading_student_meta: false,
    current_student_meta: null,
    loading_rooms: false,
  }),
  getters: {
    notification_count: (state) => state.notifications.length || 0,
    has_notifications: (state) => state.notifications.length > 0,
    can_view: () => {
      const auth = useAuthStore();
      return auth.user.hasPermissionIn(["leydenmobile-view"]);
    },
  },
  actions: {
    async loadConfig() {
      const esp_config = await SystemMetaResource.$find("eschool");
      //console.log("esp_config", esp_config);
      this.watchlist_students = esp_config.watchlist_students.map(
        (item) => new WatchlistStudentResource(item)
      );
      this.notifications = esp_config.notifications.map(
        (item) => new NotificationResource(item)
      );
      this.student_filter_options = esp_config.student_filter_options;
      this.note_type_options = esp_config.note_types.map((row) => {
        return {
          value: row.CODE,
          label: `${row.CODE.trim()} - ${row.DESCRIPTION}`,
        };
      });
      //this.loadWatchlist();
      //this.loadNotifications();
    },
    async loadWatchlist() {
      this.loading_watchlist = true;
      try {
        this.watchlist_students = await WatchlistStudentResource.$all();
      } catch (error) {
        console.log(error);
      }
      this.loading_watchlist = false;
    },
    async loadNotifications() {
      this.loading_notifications = true;
      try {
        this.notifications = await NotificationResource.$all();
        console.log(this.notifications);
      } catch (error) {
        console.log(error);
      }
      this.loading_notifications = false;
    },
    async loadStudent(studentId) {
      this.loading_student = true;
      try {
        this.current_student = await StudentResource.$find(studentId);
        //console.log("student", this.current_student);
        this.loadStudentMeta(studentId);
      } catch (error) {
        console.log(error);
      }
      this.loading_student = false;
    },
    async loadStudentMeta(studentId) {
      this.loading_student_meta = true;
      try {
        this.current_student_meta = await StudentMetaResource.$find(studentId);
        //console.log("meta", this.current_student_meta);
      } catch (error) {
        console.log(error);
      }
      this.loading_student_meta = false;
    },
    isStudentInWatchlist(studentId) {
      return (
        this.watchlist_students.findIndex(
          (stu) => stu.STUDENT_ID.trim() === studentId.trim()
        ) !== -1
      );
    },
    toggleWatchlist(studentId) {
      if (this.isStudentInWatchlist(studentId)) {
        return this.watchlistRemoveStudent(studentId);
      } else {
        return this.watchlistAddStudent(studentId);
      }
    },
    async watchlistAddStudent(studentId) {
      const request = new WatchlistStudentResource({ STUDENT_ID: studentId });
      const model = await request.save();
      //console.log("adding to watchlist", model);
      this.savedWatchlist(model);
    },
    async watchlistRemoveStudent(studentId) {
      const request = new WatchlistStudentResource({ STUDENT_ID: studentId });
      const response = await request.delete();
      const model = new WatchlistStudentResource(response.data.data);
      //console.log("removing from watchlist", model, response);
      this.removedWatchlist(model);
    },
    async notificationRemove(notificationId) {
      const notification = new NotificationResource({ PAM_ID: notificationId });
      const response = await notification.delete();
      //console.log("removing from notification", notification, response);
      if (response.data.notifications) {
        this.notifications = response.data.notifications.map(
          (n) => new NotificationResource(n)
        );
      } else {
        this.removeRelatedData(
          notification,
          "notifications",
          (i) => i.PAM_ID == notificationId
        );
      }
    },
    async loadStaff(username) {
      this.loading_staff = true;
      try {
        const response = await StaffResource.find(username);
        this.current_staff = new StaffResource(response.data);
        this.current_staff_schedule = response.schedule;
        console.log(response, this.current_staff, this.current_staff_schedule);
      } catch (error) {
        console.log(error);
      }
      this.loading_staff = false;
    },
    async loadRooms(buildingId) {
      this.loading_rooms = true;
      try {
        this.building_rooms = [];
        const building = new BuildingResource({ BUILDING: buildingId });
        this.building_rooms = await building
          .rooms()
          .where("is_classroom", 1)
          .where("regular_school_year", 1)
          .$get();
      } catch (error) {
        console.log(error);
      }
      this.loading_rooms = false;
    },

    /**
     *  Handle Changes to Related Data
     **/
    updateRelatedData(model, myKey, matchFn) {
      //console.log("updateRelatedData", model, myKey, matchFn);
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex == -1) {
        this[myKey].push(model);
      } else {
        this[myKey].splice(dataIndex, 1, model);
      }
    },

    removeRelatedData(model, myKey, matchFn) {
      //console.log("removeRelatedData", model, myKey, matchFn);
      if (typeof this[myKey] != "object" || !Array.isArray(this[myKey])) {
        // Don't care about save.
        return;
      }

      const dataIndex = this[myKey].findIndex(matchFn);
      if (dataIndex != -1) {
        this[myKey].splice(dataIndex, 1);
      }
    },
    updateStudentMetaRelatedData(model, myKey, matchFn) {
      //console.log("updateRelatedData", model, myKey, matchFn);
      if (
        typeof this.current_student_meta[myKey] != "object" ||
        !Array.isArray(this.current_student_meta[myKey])
      ) {
        // Don't care about save.
        return;
      }

      const dataIndex = this.current_student_meta[myKey].findIndex(matchFn);
      if (dataIndex == -1) {
        this.current_student_meta[myKey].push(model);
      } else {
        this.current_student_meta[myKey].splice(dataIndex, 1, model);
      }
    },

    removeStudentMetaRelatedData(model, myKey, matchFn) {
      //console.log("removeRelatedData", model, myKey, matchFn);
      if (
        typeof this.current_student_meta[myKey] != "object" ||
        !Array.isArray(this.current_student_meta[myKey])
      ) {
        // Don't care about save.
        return;
      }

      const dataIndex = this.current_student_meta[myKey].findIndex(matchFn);
      if (dataIndex != -1) {
        this.current_student_meta[myKey].splice(dataIndex, 1);
      }
    },

    // Updated Data
    savedWatchlist(student) {
      const model = new WatchlistStudentResource(student);

      this.updateRelatedData(
        model,
        "watchlist_students",
        (i) => i.STUDENT_ID.trim() == model.STUDENT_ID.trim()
      );
    },

    removedWatchlist(student) {
      this.removeRelatedData(
        student,
        "watchlist_students",
        (i) => i.STUDENT_ID.trim() == student.STUDENT_ID.trim()
      );
    },

    savedStudentNote(note) {
      const model = new StudentNoteResource(note);

      this.updateStudentMetaRelatedData(
        model,
        "notes",
        (i) => i.record_id == model.record_id
      );
    },

    removeStudentNote(note) {
      this.removeStudentMetaRelatedData(
        note,
        "notes",
        (i) => i.record_id == note.record_id
      );
    },
  },
});
